// What counts as "outside" for a NON-MODAL side panel (BL-2087): the one rule
// both apps' message trays dismiss on, so a click behind the panel either acts
// (a control) or closes it (empty page area) — never both, never neither.

/**
 * Interactive elements and roles. A pointer that lands in one of these is the
 * operator USING the page behind the panel — opening a lot, pressing a button —
 * so the panel stays open (BL-2087 AC 2).
 */
const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="textbox"]',
  '[role="combobox"]',
].join(',');

/**
 * Another overlay's surface. A pointer inside one belongs to that overlay; the
 * panel underneath it must not react.
 */
const OVERLAY_SELECTOR = '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"]';

/** Where the pointer landed inside its target, for the scrollbar check. */
export interface DismissPointer {
  offsetX?: number;
  offsetY?: number;
}

/**
 * Whether the pointer hit the target's own scrollbar rather than its content.
 * Dragging the lot list's scrollbar is scrolling (AC 1), not a click-out.
 */
function isScrollbarHit(el: Element, pointer: DismissPointer): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const { offsetX, offsetY } = pointer;
  const vertical = offsetX != null && el.scrollHeight > el.clientHeight && offsetX >= el.clientWidth;
  const horizontal = offsetY != null && el.scrollWidth > el.clientWidth && offsetY >= el.clientHeight;
  return vertical || horizontal;
}

/**
 * Whether a pointer-down on `target` is a click on EMPTY page area — the only
 * outside click that dismisses a non-modal panel (BL-2087 AC 3). False for an
 * interactive control or anything inside one, for a node inside another
 * overlay, and for a scrollbar hit.
 *
 * The caller has already established that `target` is outside the panel.
 *
 * @param target - The element the pointer went down on.
 * @param pointer - The pointer's `offsetX` / `offsetY` within `target`, when known.
 * @returns `true` when the click should close the panel.
 */
export function isNonModalDismissTarget(target: Element, pointer: DismissPointer = {}): boolean {
  if (target.closest(INTERACTIVE_SELECTOR)) return false;
  if (target.closest(OVERLAY_SELECTOR)) return false;
  return !isScrollbarHit(target, pointer);
}
